'use client';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Workspaces } from '@air/api';
import { type Workspace, type WorkspaceListResponse } from '@air/api/types';
import { reportErrorToBugsnag } from '@air/utils-error';

import { getWorkspacesListKey } from '~/hooks/api/useWorkspaces';

type SwitchWorkspaceContext = {
  previousWorkspaces: WorkspaceListResponse | undefined;
};

export const useSwitchWorkspaceMutation = () => {
  const queryClient = useQueryClient();

  const switchWorkspaceMutation = useMutation<void, Error, Pick<Workspace, 'id'>, SwitchWorkspaceContext>({
    mutationFn: ({ id }) => Workspaces.switchWorkspaces({ workspaceId: id }),
    onError: (error, { id }, context) => {
      if (context?.previousWorkspaces) {
        queryClient.setQueryData(getWorkspacesListKey(), context.previousWorkspaces);
      }

      reportErrorToBugsnag({
        context: 'Unable to switch workspace',
        error,
        metadata: {
          data: {
            workspaceId: id,
          },
          key: 'data',
        },
      });
    },
    onMutate: ({ id }) => {
      const previousWorkspaces = queryClient.getQueryData<WorkspaceListResponse>(getWorkspacesListKey());

      if (previousWorkspaces?.length) {
        queryClient.setQueryData<WorkspaceListResponse>(getWorkspacesListKey(), (data) => {
          return data
            ? data.map((workspace) =>
                workspace.id === id ? { ...workspace, lastUsed: new Date().toISOString() } : workspace,
              )
            : [];
        });
      }

      return { previousWorkspaces };
    },
  });

  return { switchWorkspaceMutation };
};
